/**
 * Rozpoznání materiálů, které se dají ukázat přímo na stránce —
 * přehrát záznam, zobrazit fotku.
 *
 * Nahrané záznamy dostanou z API v `material.media` i stopáž, náhled a stav
 * převodu. Starší ročníky mají u záznamu jen odkaz na Nextcloud — ten žádné
 * `media` nemá a zůstává obyčejným odkazem jinam.
 */
const VIDEO_EXT = ['.mp4', '.webm', '.m4v', '.ogv'];
const AUDIO_EXT = ['.mp3', '.m4a', '.wav', '.ogg', '.opus'];
const IMAGE_EXT = ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.avif', '.svg'];

/** Přípona z adresy, bez dotazu a kotvy: "/uploads/a-1f.mp4?v=2" -> ".mp4" */
function extensionOf(url) {
  const address = String(url || '').split(/[?#]/)[0];
  const dot = address.lastIndexOf('.');
  const slash = address.lastIndexOf('/');
  return dot > slash ? address.slice(dot).toLowerCase() : '';
}

/** Míří adresa na tenhle web? Cizí doménu do stránky nevkládáme. */
function isLocal(url) {
  const address = String(url || '');
  if (!address || address.startsWith('//')) return false;
  if (!/^[a-z][a-z0-9+.-]*:/i.test(address)) return true;
  return address.startsWith(`${window.location.origin}/`);
}

/**
 * Co se s materiálem dá dělat:
 *   { play: 'video' | 'audio', ready, processing, progress, duration, poster }
 * nebo null, jde-li o obyčejný odkaz.
 */
export function playback(material) {
  if (!material?.url || !isLocal(material.url)) return null;

  const info = material.media;
  const ext = extensionOf(material.url);
  const kind = info?.kind
    || (VIDEO_EXT.includes(ext) ? 'video' : AUDIO_EXT.includes(ext) ? 'audio' : null);
  if (!kind) return null;

  const status = info?.status || 'ready';
  return {
    play: kind,
    ready: status === 'ready',
    processing: status === 'queued' || status === 'processing',
    failed: status === 'failed',
    progress: info?.progress || 0,
    duration: info?.duration || null,
    poster: info?.poster || null,
  };
}

/**
 * Dá se materiál ukázat jako fotka rovnou ve stránce? Jen nahrané obrázky —
 * odkaz na cizí doménu zůstává odkazem, stejně jako u záznamů.
 */
export function isPhoto(material) {
  if (material?.kind !== 'photo' || !material.url) return false;
  return isLocal(material.url) && IMAGE_EXT.includes(extensionOf(material.url));
}

/** Vteřiny na "1:02:03" nebo "12:34". */
export function formatClock(seconds) {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const total = Math.floor(seconds);
  const s = String(total % 60).padStart(2, '0');
  const m = Math.floor(total / 60) % 60;
  const h = Math.floor(total / 3600);
  return h ? `${h}:${String(m).padStart(2, '0')}:${s}` : `${m}:${s}`;
}

/** Stopáž do popisku štítku: 2940 -> "49 min". */
export function formatLength(seconds) {
  if (!Number.isFinite(seconds) || seconds < 60) return '';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? `${hours} h ${rest} min` : `${hours} h`;
}

/** Bajty na čitelnou velikost: 1536 -> "2 kB", 1.5e9 -> "1,40 GB" */
export function formatSize(bytes) {
  if (!Number.isFinite(bytes)) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} kB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1).replace('.', ',')} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2).replace('.', ',')} GB`;
}
